import type { CSSProperties } from "react";

export type OrbState = "planning" | "developing" | "ci" | "approval" | "error";

/** color = token de tema; speed = segundos por vuelta (0 = sin giro); glow = segundos por pulso del halo (0 = sin halo). */
const ORB: Record<OrbState, { color: string; speed: number; glow: number }> = {
  planning: { color: "ac", speed: 3.6, glow: 2.4 },
  developing: { color: "gd", speed: 2.2, glow: 1.6 },
  ci: { color: "in", speed: 3, glow: 2 },
  approval: { color: "wn", speed: 6, glow: 3 },
  error: { color: "er", speed: 0, glow: 0 },
};

const abs: CSSProperties = { position: "absolute", inset: 0 };

/** Orbe del agente: sustituye al punto pulsante. Decorativo; la etiqueta de estado siempre va al lado. Su movimiento no se apaga con reduce-motion: es el indicador de actividad. */
export function ThinkingOrb({ state, size = 14 }: { state: OrbState; size?: number }) {
  const { color, speed, glow } = ORB[state];
  const c = `var(--${color})`;
  const spin = (name: string, s: number): CSSProperties => ({ ...abs, animation: speed ? `${name} ${s}s linear infinite` : "none" });
  const blur = `blur(${Math.max(1, size * 0.07)}px)`;
  return (
    <span
      aria-hidden
      className="orb"
      style={{
        position: "relative", display: "inline-block", flex: "none", width: size, height: size, borderRadius: "50%",
        ["--orb-c" as string]: c,
        animation: glow ? `orb-glow ${glow}s ease-in-out infinite` : "none",
      }}
    >
      <span style={{ ...abs, borderRadius: "50%", overflow: "hidden", background: `color-mix(in srgb, ${c} 14%, var(--bg))` }}>
        {/* estela cónica: el barrido de luz es lo que hace evidente el giro */}
        <span style={{ ...spin("orb-spin", speed), background: `conic-gradient(from 0deg, transparent 0deg, ${c} 70deg, transparent 150deg, color-mix(in srgb, ${c} 60%, #fff) 230deg, transparent 300deg)`, filter: blur, opacity: 0.95 }} />
        <span style={{ ...spin("orb-spin-r", speed * 1.7), filter: blur }}>
          <span style={{ position: "absolute", left: "8%", top: "12%", width: "58%", height: "58%", borderRadius: "50%", background: `radial-gradient(circle, color-mix(in srgb, ${c} 50%, #fff) 0%, ${c} 35%, transparent 72%)` }} />
        </span>
        <span style={{ ...abs, background: "radial-gradient(circle at 30% 24%, rgba(255,255,255,.4), transparent 42%)" }} />
      </span>
    </span>
  );
}
